<script setup lang="ts">
import useMapData from '~/composables/map/useMap'
import useMapDrawer from '~/composables/map/useMapDrawer'
import type { ExtendedMarker, ZoneStation } from '~/interfaces/map'
import PoolDrawer from '../Pools/PoolDrawer.vue'
import PoolPreview from '~/components/Pools/PoolPreview.vue'
import PoolPricesPanel from '../Pools/PoolPricesPanel.vue'
import PoolSimulatorPanel from '../Pools/PoolSimulatorPanel.vue'
import PoolSimulatorVehicleManagement from '../Pools/PoolSimulatorVehicleManagement.vue'
import useUserStore from '~/store/user'
import usePoolStore from '~/store/pool'
import useVehiclesStore from '~/store/vehicles'
import useSimulationStore from '~/store/simulation'
import { useBreakpoints } from '@vueuse/core'
import type { LatLngTuple } from 'leaflet'
import { PoolPanelState as DrawerState } from '~/interfaces/poolDetail'
import { VehicleManagementView } from '~/interfaces/vehicles'
import type { SimulationPoolConnector } from '#cmui/types/simulation'

const config = useRuntimeConfig()

const userStore = useUserStore()
const poolStore = usePoolStore()
const vehiclesStore = useVehiclesStore()
const simulationStore = useSimulationStore()

const S3_ASSETS_URL = config.public.S3_ASSETS_URL as string
const LEGACY_URL = config.public.LEGACY_URL as string

export interface Props {
    minZoom?: number
    defaultStation?: ZoneStation | undefined
    defaultZoom?: number
    defaultCenter?: LatLngTuple | undefined
    networkId?: number | undefined
    city?: string | undefined
    countryId?: number | undefined
    canChargeWithChargemap?: boolean | undefined
    isHeightLimited?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    minZoom: 2,
    defaultStation: undefined,
    defaultZoom: 6.5,
    defaultCenter: undefined,
    networkId: undefined,
    city: undefined,
    countryId: undefined,
    canChargeWithChargemap: undefined,
    isHeightLimited: true
})

const poolDrawer = useTemplateRef('poolDrawer')
const breakpoints = useBreakpoints({ tablet: 768 })
const isDesktopOrTablet = breakpoints.isGreaterOrEqual('tablet')
const isLoading = ref(false)

const {
    getMapData,
    mapKeys,
    updatedBounds,
    mapCenter,
    selectedMarker,
    markerList,
    getKeyData,
    markersData
} = useMapData(
    props.defaultCenter,
    props.networkId,
    props.city,
    props.countryId,
    props.canChargeWithChargemap
)

const { drawerVisible, currentDrawerState, setDrawerState } = useMapDrawer()

const hide = () => {
    drawerVisible.value = false
    selectedMarker.value = undefined
}

const markerClicked = async (marker: ExtendedMarker) => {
    if (marker.id === selectedMarker.value?.id) {
        return
    }

    if (marker.type !== 'cluster') {
        const slug = markersData.value?.find(
            (stationToFind) => stationToFind.pool?.id === marker.id
        )?.pool.slug as string
        poolStore.setPoolSlug(slug)
        simulationStore.reset()
        selectedMarker.value = marker

        await poolStore.fetchPoolDetailFromPoolDetail()

        if (poolStore.isDeleted && !userStore.isAdmin) {
            hide()
        } else {
            await poolStore.fetchChargingCompatibilitiesFromMappy()
        }
        isLoading.value = false
    }
}

const handleSwipeRight = () => {
    if (currentDrawerState.value === DrawerState.VehicleSelection) {
        if (
            vehiclesStore.isVehicleFormView ||
            vehiclesStore.isVehicleDeletionView
        ) {
            if (
                simulationStore.simulator.currentVehicle?.isIncompatible ||
                simulationStore.vehicles.length === 0
            ) {
                setDrawerState(DrawerState.Prices)
            } else {
                vehiclesStore.setVehicleManagementView(
                    VehicleManagementView.LIST
                )
            }

            vehiclesStore.vehicleForModification = null
            vehiclesStore.vehicleForDeletion = null
        } else {
            if (simulationStore.simulator.currentVehicle?.isIncompatible) {
                setDrawerState(DrawerState.Prices)
            } else {
                setDrawerState(DrawerState.Simulator)
            }
        }
    } else if (currentDrawerState.value === DrawerState.ConnectorSelection) {
        setDrawerState(DrawerState.Simulator)
    } else if (currentDrawerState.value === DrawerState.Simulator) {
        setDrawerState(DrawerState.Prices)
    } else if (currentDrawerState.value === DrawerState.Prices) {
        setDrawerState(DrawerState.Preview)
    }
}

const handleSwipeLeft = () => {
    if (currentDrawerState.value === DrawerState.Preview) {
        setDrawerState(DrawerState.Prices)
    } else if (currentDrawerState.value === DrawerState.Prices) {
        setDrawerState(DrawerState.Simulator)
    }
}

const handleConnectorSelection = (connectorId: number) => {
    simulationStore.selectConnector(connectorId)
    setTimeout(() => {
        handleSwipeRight()
    }, 300)
}

const openDrawer = async (selectedMarker: ExtendedMarker) => {
    drawerVisible.value = !!selectedMarker
    setDrawerState(DrawerState.Preview)

    if (poolStore.canChargeWithChargemapPass) {
        await userStore.fetchUserVehicles()
    }
}

watchEffect(async () => {
    if (selectedMarker.value) {
        isLoading.value = true

        await openDrawer(selectedMarker.value)
    }
})

watch(currentDrawerState, () => {
    if (drawerVisible.value && poolDrawer.value) {
        poolDrawer.value.$el.scrollTop = 0
    }
})

onMounted(() => {
    getKeyData()
    getMapData()
})
</script>

<template>
    <div
        class="w-full relative overflow-hidden z-0"
        :class="[isHeightLimited ? 'h-[360px] md:h-[600px]' : 'h-full']"
    >
        <PoolDrawer
            v-if="drawerVisible"
            ref="poolDrawer"
            class="absolute z-20 w-full md:w-1/3 h-full bg-white overflow-y-auto border"
            :class="[isHeightLimited ? 'rounded-l-[20px]' : '']"
            :show-close-button="!isLoading"
            :display-swipe-right="currentDrawerState !== DrawerState.Preview"
            is-button-absolute
            @closePreview="hide"
            @swipeToTheRight="handleSwipeRight"
            @swipeToTheLeft="handleSwipeLeft"
        >
            <PoolPreview
                v-if="currentDrawerState === DrawerState.Preview"
                :is-tablet="isDesktopOrTablet"
                :is-loading
                is-map
                @closePreview="hide"
                @showPricesInDrawer="setDrawerState(DrawerState.Prices)"
            />
            <PoolPricesPanel
                v-if="currentDrawerState === DrawerState.Prices"
                @showSimulatorPanel="handleSwipeLeft"
                @showVehicleSelection="
                    setDrawerState(DrawerState.VehicleSelection)
                "
            />
            <PoolSimulatorPanel
                v-if="currentDrawerState === DrawerState.Simulator"
                @showVehicleSelection="
                    setDrawerState(DrawerState.VehicleSelection)
                "
                @showConnectorSelection="
                    setDrawerState(DrawerState.ConnectorSelection)
                "
            />
            <SimulationConnectorsList
                v-if="currentDrawerState === DrawerState.ConnectorSelection"
                class="py-8 px-6 h-full overflow-y-auto"
                :connectors="
                    simulationStore.connectors as SimulationPoolConnector[]
                "
                :s3-url="S3_ASSETS_URL"
                :legacy-api-url="LEGACY_URL"
                :has-incompatibility="simulationStore.hasIncompatibleConnector"
                @selectConnector="handleConnectorSelection($event.id)"
            />
            <PoolSimulatorVehicleManagement
                v-if="currentDrawerState === DrawerState.VehicleSelection"
                class="h-fit py-8 px-6"
                @showSimulatorPanel="handleSwipeRight"
            />
        </PoolDrawer>

        <ChargemapMap
            v-if="!!mapCenter"
            :marker-list
            :map-center
            :min-zoom
            :map-zoom="defaultZoom"
            :key-groups="mapKeys"
            :tiles-url="`https://tile.jawg.io/jawg-sunny/{z}/{x}/{y}.png?access-token=${config.public.JAWG_TOKEN}`"
            :default-selected-marker="selectedMarker"
            :key-title="$t('map.key.title')"
            @boundsUpdated="updatedBounds"
            @markerClicked="markerClicked"
        />
    </div>
</template>
